import {
  OrganizationService,
  Organization as OrganizationType,
  UpdateOrgRequest,
} from '@/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useStore } from '@nanostores/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Save } from 'lucide-react';
import { toast } from 'sonner';
import { ManageOrganization } from './manage-org';
import { UpdateOrgLogoDialog } from '@/components/UpdateOrgLogoDialog';

export type OrganizationProps = {
  org: OrganizationType | undefined;
  setOrg: React.Dispatch<React.SetStateAction<OrganizationType | undefined>>;
};



export const Organization = () => {
  const selectedOrg = useStore($orgInSettings);
  const selectedOrgInApp = useStore($org);
  const orgs = useStore($orgs);
  const queryClient = useQueryClient();
  const [editingOrgName, setEditingOrgName] = useState(false);
  const [orgName, setOrgName] = useState(selectedOrg?.name || '');
  const [isLogoDialogOpen, setIsLogoDialogOpen] = useState(false); // New state for dialog



  // mutation to create a new organization
  const updateOrg = useMutation({
    mutationFn: async (payload: UpdateOrgRequest) => {
      // call the api to create the organization
      const res = await OrganizationService.updateOrganization(
        selectedOrg?.id || '',
        payload,
      );

      return res;
    },
    onSuccess: async data => {
      // invalidate queries here so that they are refetched
      await queryClient.invalidateQueries({
        queryKey: ['getOrganizations'],
      });
      $orgInSettings.set(data?.organization);
      if (selectedOrgInApp?.id === selectedOrg?.id) {
        $org.set(data?.organization);
      }
      setEditingOrgName(false);
    },
    onError: error => {
      console.error(error);
      toast('An error occurred', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  // Ensure the organization logo URL is up-to-date
  useEffect(() => {
    if (selectedOrg?.id) {
      ensureOrgLogoUrl(selectedOrg?.id);
    }
  }, [selectedOrg?.id]);
  const orgLogoUrls = useStore($orgLogoUrls);
  const orgLogoUrl = orgLogoUrls[selectedOrg?.id || ''];    

  return (
    <div>
      <div className="flex w-full flex-col gap-3">
        <button
          key={selectedOrg?.id}
          className={cn(
            'flex flex-col items-start gap-2 rounded-lg border p-4 text-left text-sm transition-all',
          )}
        >
          <div className="flex w-full items-center gap-4">
            <Avatar className="h-10 w-10 rounded-md" onClick={() => setIsLogoDialogOpen(true)}>
              <AvatarImage src={orgLogoUrl?.url || ''} />
              <AvatarFallback>
                {selectedOrg?.name.slice(0, 2).toLocaleUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex w-full flex-col gap-1">
              <div className="flex flex-1 items-center">
                <div className="flex flex-col items-start justify-center">
                  {editingOrgName ? (
                    <Input
                      size={30}
                      className="h-6"
                      value={orgName}
                      onChange={e => {
                        setOrgName(e?.target?.value);
                      }}
                      onKeyDown={e => {
                        if (e?.key === 'Enter') {
                          if (selectedOrg) {
                            updateOrg.mutateAsync({ name: orgName, logoBase64: '', logoFileName: '' });
                            // setEditingOrgName(false);
                          }
                        }
                      }}
                      disabled={updateOrg?.isPending}
                    />
                  ) : (
                    <div className="text-sm font-semibold">
                      {selectedOrg?.name}
                    </div>
                  )}

                  {selectedOrg?.createdBy && (
                    <div className="text-muted-foreground">
                      Created by {selectedOrg.createdBy.name}
                    </div>
                  )}
                </div>
                <div
                  className={cn(
                    'ml-auto items-center p-1 text-muted-foreground hover:rounded-md ',
                    'flex flex-col items-end justify-center',
                  )}
                >
                  {editingOrgName ? (
                    <div className="flex">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingOrgName(!editingOrgName);
                          setOrgName(selectedOrg?.name || '');
                        }}
                        disabled={updateOrg?.isPending}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          updateOrg.mutateAsync({ name: orgName ,logoBase64: '',
                            logoFileName: ''});
                        }}
                        disabled={updateOrg?.isPending}
                      >
                        {updateOrg?.isPending ? (
                          <Loader2 className="ml-0 h-3 animate-spin p-0" />
                        ) : (
                          <Save className="ml-0 h-3 p-0" />
                        )}
                        Save
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingOrgName(!editingOrgName);
                      }}
                    >
                      <Pencil className="ml-0 h-3 p-0" /> Edit
                    </Button>
                  )}
                  <div className="text-xs">
                    Created on{' '}
                    {new Date(
                      selectedOrg?.createdAtUtc || '',
                    ).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </button>
      </div>
      {/* Logo Update Dialog */}
      {selectedOrg && (
        <UpdateOrgLogoDialog
          open={isLogoDialogOpen}
          setOpen={setIsLogoDialogOpen}
          orgId={selectedOrg.id} // Added missing orgId property
        />
      )}
      <div className="mt-6">
        <ManageOrganization organizationId={selectedOrg?.id || 'no'} />
      </div>
    </div>
  );
};
